import { useQuery, useMutation, useAction } from "convex/react";
import { convexApi } from "@/lib/convex-api";
import { useAuth } from "@/lib/auth-provider";
import { getIsDev, getIsNativePlatform, getIsProductionDomain, getIsSatelliteDomain } from "@/lib/domain-utils";

/**
 * Determines whether to skip Convex queries.
 *
 * Security layer: Even if VITE_AUTH_ENABLED=false is accidentally deployed to production,
 * the Convex backend will still enforce authentication. This client-side check is just
 * for UX (showing loading states vs login prompts), not the security boundary.
 *
 * Rules:
 * 1. Production domains: Never skip - always attempt queries (Convex enforces auth)
 * 2. Dev environment: Allow queries (Convex has dev-bypass when ALLOW_DEV_AUTH_BYPASS=true)
 * 3. Native apps: Never skip - always attempt queries
 */
export function shouldSkipConvexQueries(): boolean {
  const isNative = getIsNativePlatform();
  const isProd = getIsProductionDomain();
  const isDevEnv = getIsDev();

  // Never skip on production - Convex backend enforces auth regardless of env vars
  if (isProd) {
    return false;
  }

  // Never skip on native apps - they handle auth separately
  if (isNative) {
    return false;
  }

  // Dev environment: Allow queries for testing
  // Convex backend has dev-bypass when ALLOW_DEV_AUTH_BYPASS=true
  if (isDevEnv) {
    return false;
  }
  
  // Default: don't skip (attempt queries, let Convex handle auth)
  return false;
}

/**
 * Check if we're in a dev environment where bypass COULD be active.
 *
 * NOTE: This only checks the environment gate. The actual bypass decision
 * is made in main.tsx after fetching the DB toggle from Convex.
 *
 * For components that need to know if bypass is actually active, use:
 * useAuth().isDevBypassMode
 *
 * Dev bypass only possible when:
 * 1. Running in dev mode (Vite DEV) - checked here
 * 2. AND DB toggle is enabled - checked in main.tsx
 */
export function isDevBypassMode(): boolean {
  // Only return true if in dev mode - actual bypass requires DB toggle too
  // This is a synchronous approximation; use useAuth().isDevBypassMode for accuracy
  return getIsDev();
}

export interface ConvexStaffMember {
  _id: string;
  _creationTime: number;
  siteId: string;
  userId?: string; // Clerk user ID - only present if staff member has signed up
  name: string;
  email?: string;
  phone?: string;
  roles?: string[];
  role?: string;
  barcodeId?: string;
  hourlyRate?: number;
  hireDate?: number;
  isActive: boolean;
  notes?: string;
  createdAt: number;
  color?: string;
  status?: string;
  hasRosterAccess?: boolean; // Can view all staff and manage rosters (manager view)
  avatarFileId?: string; // Convex storage ID for profile picture
  dateOfBirth?: number; // Unix timestamp of birth date
  shareBirthday?: boolean; // If true, share birthday (month/day only) with coworkers
}

export interface ConvexRosterWeek {
  _id: string;
  siteId: string;
  weekStartDate: string;
  status: "draft" | "published";
  publishedAt?: number;
  publishedByUserId?: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export interface ConvexRosterShift {
  _id: string;
  rosterWeekId: string;
  staffMemberId: string;
  date: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  hasLunchBreak: boolean;
  role?: string;
  shiftRoles?: string[];
  locationId?: string;
  notes?: string;
  staffMember?: ConvexStaffMember;
}

export interface ConvexStaffRole {
  _id: string;
  siteId: string;
  name: string;
  key: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: number;
}

export interface ConvexAvailabilityPattern {
  _id: string;
  siteId: string;
  staffMemberId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export interface ConvexAvailabilityOverride {
  _id: string;
  siteId: string;
  staffMemberId: string;
  date: string;
  isAvailable: boolean;
  startTime?: string;
  endTime?: string;
  reason?: string;
  leaveRequestId?: string;
  createdAt: number;
}

export interface ConvexUserSite {
  siteId: string;
  siteName: string;
  organizationId: string;
  organizationName: string;
  role: "owner" | "manager" | "staff";
  isActive: boolean;
}

export function useConvexUserSites() {
  const skipQueries = shouldSkipConvexQueries();
  const { user } = useAuth();
  return useQuery(
    convexApi.organizations.getUserSites,
    skipQueries ? "skip" : { email: user?.email || undefined }
  ) as ConvexUserSite[] | undefined;
}

export function useConvexCreateOrganization() {
  return useMutation(convexApi.organizations.createOrganizationWithSite);
}

export function useConvexStaff(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staff.listStaffMembers,
    skipQueries ? "skip" : (siteId ? { siteId, includeInactive: false } : "skip")
  ) as ConvexStaffMember[] | undefined;
}

export function useConvexStaffRoles(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffRoles.listRoles,
    skipQueries ? "skip" : (siteId ? { siteId, includeInactive: false } : "skip")
  ) as ConvexStaffRole[] | undefined;
}

export function useConvexRosterWeek(siteId: string | null, weekStartDate: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staff.getRosterWeek,
    skipQueries ? "skip" : (siteId && weekStartDate ? { siteId, weekStartDate } : "skip")
  ) as ConvexRosterWeek | null | undefined;
}

export function useConvexRosterShifts(rosterWeekId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staff.getRosterShifts,
    skipQueries ? "skip" : (rosterWeekId ? { rosterWeekId } : "skip")
  ) as ConvexRosterShift[] | undefined;
}

export function useConvexAvailabilityPatterns(staffMemberId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffAvailability.getPatternsByStaff,
    skipQueries ? "skip" : (staffMemberId ? { staffMemberId } : "skip")
  ) as ConvexAvailabilityPattern[] | undefined;
}

export function useConvexAllStaffAvailability(siteId: string | null, weekStartDate: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffAvailability.getAllStaffAvailabilityForWeek,
    skipQueries ? "skip" : (siteId && weekStartDate ? { siteId, weekStartDate } : "skip")
  ) as Record<string, { patterns: ConvexAvailabilityPattern[]; overrides: ConvexAvailabilityOverride[] }> | undefined;
}

// Alternative: Get all patterns for a site (more reliable than combined query)
export function useConvexSitePatterns(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffAvailability.getPatternsBySite,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexAvailabilityPattern[] | undefined;
}

export function useConvexAvailabilityOverrides(siteId: string | null, startDate: string | null, endDate: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffAvailability.getOverridesBySiteAndDateRange,
    skipQueries ? "skip" : (siteId && startDate && endDate ? { siteId, startDate, endDate } : "skip")
  ) as ConvexAvailabilityOverride[] | undefined;
}

export function useConvexCreateShift() {
  return useMutation(convexApi.staff.createShift);
}

export function useConvexUpdateShift() {
  return useMutation(convexApi.staff.updateShift);
}

export function useConvexDeleteShift() {
  return useMutation(convexApi.staff.deleteShift);
}

export function useConvexCreateRosterWeek() {
  return useMutation(convexApi.staff.createRosterWeek);
}

export function useConvexPublishRosterWeek() {
  return useMutation(convexApi.staff.publishRosterWeek);
}

export function useConvexUnpublishRosterWeek() {
  return useMutation(convexApi.staff.unpublishRosterWeek);
}

export function useConvexCopyShiftsFromWeek() {
  return useMutation(convexApi.staff.copyShiftsFromWeek);
}

export function useConvexCreateStaff() {
  return useMutation(convexApi.staff.createStaffMember);
}

export function useConvexUpdateStaff() {
  return useMutation(convexApi.staff.updateStaffMember);
}

export function useConvexDeleteStaff() {
  return useMutation(convexApi.staff.deleteStaffMember);
}

export function useConvexGenerateUploadUrl() {
  return useMutation(convexApi.storage.generateUploadUrl);
}

export function useConvexUpsertAvailabilityPattern() {
  return useMutation(convexApi.staffAvailability.upsertPattern);
}

export function useConvexSetWeeklyPattern() {
  return useMutation(convexApi.staffAvailability.setWeeklyPattern);
}

export function useConvexCreateAvailabilityOverride() {
  return useMutation(convexApi.staffAvailability.createOverride);
}

export function useConvexSaveWeekOverrides() {
  return useMutation(convexApi.staffAvailability.saveWeekOverrides);
}

export interface ConvexLeaveRequest {
  _id: string;
  siteId: string;
  staffMemberId: string;
  startDate: string;
  endDate: string;
  leaveType: "annual" | "sick" | "personal" | "unpaid" | "other";
  status: "pending" | "approved" | "rejected" | "cancelled";
  isPartialDay: boolean;
  partialStartTime?: string;
  partialEndTime?: string;
  reason?: string;
  reviewNotes?: string;
  reviewedByUserId?: string;
  reviewedAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface ConvexShiftRequest {
  _id: string;
  siteId?: string;
  // Backend uses originalShiftId/originalStaffId naming
  originalShiftId: string;
  originalStaffId: string;
  targetStaffId: string;
  targetShiftId?: string; // For swap requests: the shift being swapped with
  // More granular status flow from backend (includes legacy "pending" for backwards compatibility)
  status: "pending" | "pending_target" | "target_accepted" | "target_rejected" | "pending_manager" | "approved" | "rejected" | "cancelled";
  reason?: string;
  reviewedByUserId?: string;
  reviewedAt?: number;
  createdAt?: number;
  _creationTime?: number;
  // Populated fields for display (enriched by backend)
  shift?: {
    date: string;
    startTime: string;
    endTime: string;
    breakMinutes?: number;
  };
  targetShift?: {
    date: string;
    startTime: string;
    endTime: string;
    breakMinutes?: number;
  };
  requesterName?: string;
  targetStaffName?: string;
}

// Site settings for shift swap rules and other site-level preferences
export interface ConvexSiteSettings {
  _id: string;
  siteId: string;
  shiftSwapRoleRestriction: boolean; // If true, shifts can only swap with same-role shifts
  shiftSwapHourLimit: number | null; // Max hour difference allowed (null = no limit)
  clockReminderEnabled?: boolean; // If true (default), show clock in/out reminders on dashboard
  updatedAt: number;
  // Feature toggles - all default to true if not set
  features?: {
    calendar?: boolean;
    meetings?: boolean;
    tasks?: boolean;
    projects?: boolean;
    reminders?: boolean;
    training?: boolean;
    procedures?: boolean;
  };
}

export interface ConvexStaffInvite {
  _id: string;
  siteId: string;
  code: string;
  siteName: string;
  expiresAt: number;
  usedBy?: string;
  createdByUserId?: string;
  createdAt: number;
}

export function useConvexLeaveRequests(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.leaveRequests.getBySite,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexLeaveRequest[] | undefined;
}

export function useConvexPendingLeaveRequests(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.leaveRequests.getPending,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexLeaveRequest[] | undefined;
}

export function useConvexStaffLeaveRequests(staffMemberId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.leaveRequests.getByStaff,
    skipQueries ? "skip" : (staffMemberId ? { staffMemberId } : "skip")
  ) as ConvexLeaveRequest[] | undefined;
}

export function useConvexShiftRequests(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  // Uses shiftSwapRequests:getBySite (not shiftRequests) - has dev bypass
  return useQuery(
    convexApi.shiftSwapRequests.getBySite,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexShiftRequest[] | undefined;
}

export function useConvexPendingShiftRequests(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  // Uses shiftSwapRequests:getPending (not shiftRequests) - has dev bypass
  return useQuery(
    convexApi.shiftSwapRequests.getPending,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexShiftRequest[] | undefined;
}

export function useConvexStaffInvite(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  const devBypass = isDevBypassMode();
  // Skip in dev bypass mode - staffInvites doesn't have dev bypass support
  return useQuery(
    convexApi.staffInvites.getActiveInvite,
    skipQueries || devBypass ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexStaffInvite | null | undefined;
}

export function useConvexInviteByCode(code: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  const devBypass = isDevBypassMode();
  // Skip in dev bypass mode - staffInvites doesn't have dev bypass support
  return useQuery(
    convexApi.staffInvites.getByCode,
    skipQueries || devBypass ? "skip" : (code ? { code } : "skip")
  ) as ConvexStaffInvite | null | undefined;
}

export function useConvexApproveShiftRequest() {
  // Uses shiftSwapRequests:approveAsManager - has dev bypass
  return useMutation(convexApi.shiftSwapRequests.approveAsManager);
}

export function useConvexRejectShiftRequest() {
  // Uses shiftSwapRequests:rejectAsManager - has dev bypass
  return useMutation(convexApi.shiftSwapRequests.rejectAsManager);
}

export function useConvexRespondAsTarget() {
  // Uses shiftSwapRequests:respondAsTarget - for target staff to accept/reject
  return useMutation(convexApi.shiftSwapRequests.respondAsTarget);
}

export function useConvexApproveLeaveRequest() {
  return useMutation(convexApi.leaveRequests.approve);
}

export function useConvexRejectLeaveRequest() {
  return useMutation(convexApi.leaveRequests.reject);
}

export function useConvexCreateLeaveRequest() {
  return useMutation(convexApi.leaveRequests.create);
}

export function useConvexCancelLeaveRequest() {
  return useMutation(convexApi.leaveRequests.cancel);
}

export function useConvexUseInvite() {
  return useMutation(convexApi.staffInvites.use);
}

// Shift swap request hooks
export function useConvexCreateShiftSwapRequest() {
  // Uses shiftSwapRequests:create (not shiftRequests:create) - has dev bypass
  return useMutation(convexApi.shiftSwapRequests.create);
}

export function useConvexCancelShiftRequest() {
  // Uses shiftSwapRequests:cancel - has dev bypass
  return useMutation(convexApi.shiftSwapRequests.cancel);
}

export function useConvexStaffShiftRequests(staffMemberId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  // shiftRequests:getByStaff has dev bypass
  return useQuery(
    convexApi.shiftRequests.getByStaff,
    skipQueries ? "skip" : (staffMemberId ? { staffMemberId } : "skip")
  ) as ConvexShiftRequest[] | undefined;
}

// Site settings for swap rules
export function useConvexSiteSettings(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.siteSettings.get,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexSiteSettings | null | undefined;
}

// Site feature toggles for enabling/disabling features per site
export interface ConvexSiteFeatureToggles {
  _id: string | null;
  siteId: string;
  messagingEnabled: boolean;
  noticeBoardEnabled: boolean;
  tasksEnabled: boolean;
  projectsEnabled: boolean;
  proceduresEnabled: boolean;
  meetingsEnabled: boolean;
  clockInEnabled: boolean;
  trainingEnabled: boolean;
  availabilityEnabled: boolean;
  leaveEnabled: boolean;
  shiftSwapsEnabled: boolean;
  remindersEnabled: boolean;
  calendarEnabled: boolean;
  weatherEnabled: boolean;
  calendarSyncEnabled: boolean;
  updatedAt: number | null;
  updatedByUserId: string | null;
}

export function useConvexSiteFeatureToggles(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.siteFeatures.get,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexSiteFeatureToggles | null | undefined;
}

export function useConvexToggleFeature() {
  return useMutation(convexApi.siteFeatures.toggleFeature);
}

export function useConvexUpdateFeatures() {
  return useMutation(convexApi.siteFeatures.update);
}

// Clock-in/out hooks
export interface ConvexClockEvent {
  _id: string;
  _creationTime?: number;
  siteId?: string;
  staffMemberId?: string;
  timestamp: number;
  type: "clock_in" | "clock_out";
  method: "qr" | "pin" | "photo" | "nfc" | "manual";
  stationId?: string;
  photoFileId?: string;
  shiftId?: string;
}

export function useConvexClockEvents(staffMemberId: string | null, limit: number = 20) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.clockIn.getMyEvents,
    skipQueries ? "skip" : (staffMemberId ? { staffMemberId, limit } : "skip")
  ) as ConvexClockEvent[] | undefined;
}

export function useConvexClockInByQr() {
  return useMutation(convexApi.clockIn.clockInByQr);
}

export function useConvexSetStaffPin() {
  return useMutation(convexApi.clockIn.setStaffPin);
}

export function useConvexVerifyQrTokenAction() {
  return useAction(convexApi.clockInCrypto.verifyQrToken);
}

// Staff Communications Types
export interface ConvexStaffThread {
  _id: string;
  siteId: string;
  type: "tip" | "direct" | "handover";
  participants?: string[];
  terminalId?: string;
  shiftDate?: string;
  subject?: string;
  lastMessageAt: number;
  lastMessagePreview?: string; // Preview of last message
  messageCount: number;
  unreadCount?: number; // Number of unread messages for current user
  isResolved?: boolean;
  isCleared?: boolean;
  clearedByUserId?: string;
  clearedAt?: number;
  unreadByUserIds?: string[];
  createdAt: number;
  readBy?: string[];
  readAt?: Record<string, number>;
  updatedAt?: number;
  assignedDate?: string;
  body?: string;
  authorName?: string;
  readByStaff?: { staffId: string; staffName: string; readAt: number }[];
  readCount?: number;
  hasCurrentStaffRead?: boolean;
  wasUpdatedAfterRead?: boolean;
  currentStaffId?: string;
}

export interface MessageAttachment {
  fileId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
}

export interface MessageReaction {
  emoji: string;
  userIds: string[];
}

export interface ConvexStaffMessage {
  _id: string;
  siteId: string;
  threadId: string;
  authorUserId?: string;
  authorName?: string;
  authorRole?: string;
  isAnonymous: boolean;
  body: string;
  sanitizedBody?: string;
  richText?: string; // JSON rich text (Slate/TipTap format)
  attachments?: MessageAttachment[];
  reactions?: MessageReaction[];
  isFlagged?: boolean;
  flags?: string[];
  createdAt: number;
  editedAt?: number;
}

// Staff Communications Hooks - Queries
// Note: staffComms APIs use requireSiteAccess for auth
export function useConvexTips(siteId: string | null, includeResolved: boolean = true) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffComms.getTips,
    skipQueries ? "skip" : (siteId ? { siteId, includeResolved } : "skip")
  ) as (ConvexStaffThread & { messages: ConvexStaffMessage[] })[] | undefined;
}

export function useConvexDirectThreads(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffComms.getDirectThreads,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexStaffThread[] | undefined;
}

export function useConvexThreadMessages(threadId: string | null, limit?: number) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffComms.getThreadMessages,
    skipQueries ? "skip" : (threadId ? { threadId, limit: limit || 50 } : "skip")
  ) as ConvexStaffMessage[] | undefined;
}

export function useConvexHandoverNotes(siteId: string | null, terminalId?: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffComms.getHandoverNotes,
    skipQueries ? "skip" : (siteId ? { siteId, terminalId: terminalId || undefined } : "skip")
  ) as (ConvexStaffThread & { messages: ConvexStaffMessage[] })[] | undefined;
}

export function useConvexHasUnclearedHandovers(siteId: string | null, terminalId?: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffComms.hasUnclearedHandovers,
    skipQueries ? "skip" : (siteId ? { siteId, terminalId: terminalId || undefined } : "skip")
  ) as boolean | undefined;
}

// Staff Communications Hooks - Mutations
export function useConvexSendAnonymousTip() {
  return useMutation(convexApi.staffComms.sendAnonymousTip);
}

export function useConvexResolveTip() {
  return useMutation(convexApi.staffComms.resolveTip);
}

export function useConvexCreateDirectThread() {
  return useMutation(convexApi.staffComms.createDirectThread);
}

export function useConvexCreateGroupThread() {
  return useMutation(convexApi.staffComms.createGroupThread);
}

export function useConvexSendDirectMessage() {
  return useMutation(convexApi.staffComms.sendDirectMessage);
}

export function useConvexMarkThreadRead() {
  return useMutation(convexApi.staffComms.markThreadRead);
}

export function useConvexCreateHandoverNote() {
  return useMutation(convexApi.staffComms.createHandoverNote);
}

export function useConvexClearHandoverNotes() {
  return useMutation(convexApi.staffComms.clearHandoverNotes);
}

export function useConvexMarkHandoverRead() {
  return useMutation(convexApi.staffComms.markHandoverRead);
}

export function useConvexUpdateHandoverNote() {
  return useMutation(convexApi.staffComms.updateHandoverNote);
}

// Context Thread Hooks (Unified Messaging)
export interface EnrichedThread {
  _id: string;
  siteId: string;
  type: "direct" | "project" | "team" | "general" | "meeting" | "tip" | "handover";
  subject?: string;
  contextId?: string;
  contextName?: string;
  participants?: string[];
  lastMessageAt?: number;
  messageCount: number;
  createdAt: number;
  displayName: string;
  displayIcon: string;
  lastMessagePreview?: string;
  lastMessageAuthor?: string;
  isUnread: boolean;
  participantCount: number;
  unreadByUserIds?: string[];
}

export function useConvexAllThreadsForUser(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffComms.getAllThreadsForUser,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as EnrichedThread[] | undefined;
}

export function useConvexThreadsByContext(siteId: string | null, type: string | null, contextId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staffComms.getThreadsByContext,
    skipQueries ? "skip" : (siteId && type && contextId ? { siteId, type, contextId } : "skip")
  ) as EnrichedThread[] | undefined;
}

export function useConvexCreateContextThread() {
  return useMutation(convexApi.staffComms.createContextThread);
}

export function useConvexSendContextMessage() {
  return useMutation(convexApi.staffComms.sendContextMessage);
}

export function useConvexMarkContextThreadRead() {
  return useMutation(convexApi.staffComms.markContextThreadRead);
}

// Message reactions (NEW) - Enable when backend adds messaging.addReaction/removeReaction
// export function useConvexAddMessageReaction() {
//   return useMutation(convexApi.messaging.addReaction);
// }
// 
// export function useConvexRemoveMessageReaction() {
//   return useMutation(convexApi.messaging.removeReaction);
// }

// ============================================
// CALENDAR & REMINDERS
// ============================================

export interface ConvexReminder {
  _id: string;
  siteId: string;
  userId: string;
  title: string;
  description?: string;
  reminderAt: number;
  isRecurring: boolean;
  recurrencePattern?: "daily" | "weekly" | "monthly";
  status: "pending" | "sent" | "dismissed" | "snoozed";
  snoozedUntil?: number;
  notifyVia: Array<"push" | "email" | "in_app">;
  createdAt: number;
}

export interface ConvexCalendarEvent {
  _id: string;
  siteId: string;
  userId: string;
  title: string;
  description?: string;
  startAt: number;
  endAt: number;
  isAllDay: boolean;
  eventType: "personal" | "work" | "meeting" | "deadline" | "other";
  location?: string;
  color?: string;
  createdAt: number;
}

// Reminder Queries
export function useConvexMyReminders(siteId: string | null, status?: string) {
  const skipQueries = shouldSkipConvexQueries();
  const args = siteId
    ? status
      ? { siteId, status, limit: 100 }
      : { siteId, limit: 100 }
    : null;
  return useQuery(
    convexApi.reminders.listMyReminders,
    skipQueries ? "skip" : (args || "skip")
  ) as ConvexReminder[] | undefined;
}

export function useConvexUpcomingReminders(siteId: string | null, limit?: number) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.reminders.getUpcomingReminders,
    skipQueries ? "skip" : (siteId ? { siteId, limit: limit || 20 } : "skip")
  ) as ConvexReminder[] | undefined;
}

// Reminder Mutations
export function useConvexCreateReminder() {
  return useMutation(convexApi.reminders.createReminder);
}

export function useConvexUpdateReminder() {
  return useMutation(convexApi.reminders.updateReminder);
}

export function useConvexDeleteReminder() {
  return useMutation(convexApi.reminders.deleteReminder);
}

export function useConvexDismissReminder() {
  return useMutation(convexApi.reminders.dismissReminder);
}

export function useConvexSnoozeReminder() {
  return useMutation(convexApi.reminders.snoozeReminder);
}

// Calendar Event Queries
export function useConvexMyEvents(siteId: string | null, startDate: string, endDate: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.calendar.listMyEvents,
    skipQueries ? "skip" : (siteId ? { siteId, startDate, endDate, limit: 100 } : "skip")
  ) as ConvexCalendarEvent[] | undefined;
}

export function useConvexCalendarDayData(siteId: string | null, date: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.calendar.getCalendarDayData,
    skipQueries ? "skip" : (siteId && date ? { siteId, date } : "skip")
  );
}

export function useConvexCalendarWeekData(siteId: string | null, weekStart: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.calendar.getCalendarWeekData,
    skipQueries ? "skip" : (siteId && weekStart ? { siteId, weekStart } : "skip")
  );
}

export function useConvexCalendarMonthData(siteId: string | null, year: number, month: number) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.calendar.getCalendarMonthData,
    skipQueries ? "skip" : (siteId ? { siteId, year, month } : "skip")
  );
}

// Calendar Event Mutations
export function useConvexCreateEvent() {
  return useMutation(convexApi.calendar.createEvent);
}

export function useConvexUpdateEvent() {
  return useMutation(convexApi.calendar.updateEvent);
}

export function useConvexDeleteEvent() {
  return useMutation(convexApi.calendar.deleteEvent);
}

// ============================================
// MEETINGS
// ============================================

export interface ConvexMeeting {
  _id: string;
  siteId: string;
  title: string;
  description?: string;
  location?: string;
  startAt: number;
  endAt: number;
  isAllDay: boolean;
  isRecurring: boolean;
  recurrencePattern?: "daily" | "weekly" | "monthly";
  recurrenceEndDate?: string;
  reminderEnabled: boolean;
  reminderMinutesBefore?: number;
  meetingLink?: string;
  createdByUserId: string;
  createdByName: string;
  projectId?: string;
  teamRoleKeys?: string[];
  status: "scheduled" | "cancelled" | "completed";
  createdAt: number;
  updatedAt?: number;
  invites?: ConvexMeetingInvite[];
}

export interface ConvexMeetingInvite {
  _id: string;
  siteId: string;
  meetingId: string;
  invitedUserId?: string;
  invitedStaffMemberId?: string;
  invitedUserName: string;
  invitedUserEmail?: string;
  status: "pending" | "accepted" | "declined" | "tentative";
  respondedAt?: number;
  responseNote?: string;
  createdAt: number;
}

// Meeting Queries
export function useConvexMeetings(siteId: string | null, startDate?: string, endDate?: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.meetings.list,
    skipQueries ? "skip" : (siteId ? { siteId, startDate, endDate, limit: 100 } : "skip")
  ) as ConvexMeeting[] | undefined;
}

export function useConvexMyMeetings(siteId: string | null, userId: string | null, status?: string) {
  const skipQueries = shouldSkipConvexQueries();
  const args = siteId && userId
    ? status
      ? { siteId, userId, status, limit: 100 }
      : { siteId, userId, limit: 100 }
    : null;
  return useQuery(
    convexApi.meetings.listMyMeetings,
    skipQueries ? "skip" : (args || "skip")
  ) as ConvexMeeting[] | undefined;
}

export function useConvexUpcomingMeetings(siteId: string | null, limit?: number) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.meetings.getUpcoming,
    skipQueries ? "skip" : (siteId ? { siteId, limit: limit || 10 } : "skip")
  ) as ConvexMeeting[] | undefined;
}

export function useConvexPendingMeetingInvites(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.meetings.getPendingInvites,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexMeetingInvite[] | undefined;
}

export function useConvexMeetingById(meetingId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.meetings.getById,
    skipQueries ? "skip" : (meetingId ? { id: meetingId } : "skip")
  ) as ConvexMeeting | undefined;
}

// Meeting Mutations
export function useConvexCreateMeeting() {
  return useMutation(convexApi.meetings.create);
}

export function useConvexUpdateMeeting() {
  return useMutation(convexApi.meetings.update);
}

export function useConvexCancelMeeting() {
  return useMutation(convexApi.meetings.cancel);
}

export function useConvexRespondToMeetingInvite() {
  return useMutation(convexApi.meetings.respondToInvite);
}

export function useConvexAddMeetingInvitees() {
  return useMutation(convexApi.meetings.addInvitees);
}

// ============================================
// PROJECTS
// ============================================

export interface ConvexProject {
  _id: string;
  siteId: string;
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
  status: "planning" | "in_progress" | "on_hold" | "completed" | "cancelled";
  progressPercentage: number;
  ownerUserId: string;
  memberUserIds: string[];
  color?: string;
  isActive: boolean;
  createdAt: number;
  createdByUserId: string;
  updatedAt?: number;
}

// ConvexProjectTask - now uses unified "tasks" table with projectId field
// Status values changed: not_started → pending, blocked → overdue
export interface ConvexProjectTask {
  _id: string;
  siteId: string;
  projectId: string;
  name: string;
  description?: string;
  category?: string;
  priority: "low" | "medium" | "high" | "urgent";
  // For project tasks: startDate is start, dueDate is end date
  startDate?: string;
  dueDate: string;
  dueTime?: string;
  dependsOnTaskIds?: string[];
  // Assignment fields (unified with general tasks)
  assignmentType?: "specific_staff" | "shift" | "role" | "any_staff";
  assignedToUserId?: string;
  assignedToRoleIds?: string[];
  assignedUserIds?: string[]; // For backward compatibility
  // New unified status values
  status: "pending" | "in_progress" | "completed" | "overdue" | "cancelled";
  progressPercentage?: number;
  completedAt?: number;
  completedByUserId?: string;
  completedByName?: string;
  completionNotes?: string;
  sortOrder?: number;
  color?: string;
  isMilestone?: boolean;
  createdAt: number;
  createdByUserId?: string;
  updatedAt?: number;
  // Checklist items embedded in task
  checklistItems?: ConvexTaskChecklistItem[];
}

// Project Queries
export function useConvexProjects(siteId: string | null, status?: string) {
  const skipQueries = shouldSkipConvexQueries();
  const args = siteId
    ? status
      ? { siteId, status, limit: 100 }
      : { siteId, limit: 100 }
    : null;
  return useQuery(
    convexApi.projects.listProjects,
    skipQueries ? "skip" : (args || "skip")
  ) as ConvexProject[] | undefined;
}

export function useConvexProjectById(projectId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.projects.getProject,
    skipQueries ? "skip" : (projectId ? { id: projectId } : "skip")
  ) as ConvexProject | undefined;
}

export function useConvexProjectTasks(projectId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.projects.listProjectTasks,
    skipQueries ? "skip" : (projectId ? { projectId } : "skip")
  ) as ConvexProjectTask[] | undefined;
}

// Project Mutations
export function useConvexCreateProject() {
  return useMutation(convexApi.projects.createProject);
}

export function useConvexUpdateProject() {
  return useMutation(convexApi.projects.updateProject);
}

export function useConvexDeleteProject() {
  return useMutation(convexApi.projects.deleteProject);
}

export function useConvexUpdateProjectProgress() {
  return useMutation(convexApi.projects.updateProjectProgress);
}

export function useConvexCreateProjectTask() {
  return useMutation(convexApi.projects.createProjectTask);
}

export function useConvexUpdateProjectTask() {
  return useMutation(convexApi.projects.updateProjectTask);
}

export function useConvexCompleteProjectTask() {
  return useMutation(convexApi.projects.completeProjectTask);
}

export function useConvexAddProjectMember() {
  return useMutation(convexApi.projects.addProjectMember);
}

export function useConvexRemoveProjectMember() {
  return useMutation(convexApi.projects.removeProjectMember);
}

export function useConvexProjectTaskChecklistItems(projectTaskId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.projects.listProjectTaskChecklistItems,
    skipQueries ? "skip" : (projectTaskId ? { projectTaskId } : "skip")
  );
}

export function useConvexCompleteProjectTaskChecklistItem() {
  return useMutation(convexApi.projects.completeProjectTaskChecklistItem);
}

// ============================================
// TASKS (Staff Tasks - view/completion/creation)
// ============================================

export interface ConvexTask {
  _id: string;
  siteId: string;
  templateId?: string;
  projectId?: string;
  projectName?: string;
  name: string;
  description?: string;
  category?: string;
  categoryName?: string;
  priority: "low" | "medium" | "high" | "urgent";
  assignmentType: "specific_staff" | "shift" | "role" | "any_staff";
  assignedToUserId?: string;
  assignedUserIds?: string[];
  assignedToShiftId?: string;
  assignedToRoleIds?: string[];
  dueDate: string;
  dueTime?: string;
  status: "pending" | "in_progress" | "completed" | "overdue" | "cancelled";
  completedAt?: number;
  completedByUserId?: string;
  completedByName?: string;
  completionNotes?: string;
  completionPhotoId?: string;
  photoUrls?: string[];
  requiresApproval?: boolean;
  approvedAt?: number;
  approvedByUserId?: string;
  createdAt: number;
  createdByUserId: string;
  checklistItems?: ConvexTaskChecklistItem[];
}

export interface ConvexTaskChecklistItem {
  _id: string;
  taskId: string;
  projectTaskId?: string;
  label: string;
  description?: string;
  sortOrder: number;
  requiresPhoto: boolean;
  requiresValue: boolean;
  valueType?: "text" | "number" | "temperature";
  valueLabel?: string;
  isCompleted: boolean;
  completedAt?: number;
  completedByUserId?: string;
  completedByName?: string;
  enteredValue?: string;
  photoId?: string;
  photoStorageId?: string;
  notes?: string;
}

// Task Queries
// Note: listMyTasks extracts userId from auth context, only takes siteId
export function useConvexMyTasks(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.tasks.listMyTasks,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexTask[] | undefined;
}

export function useConvexTasksByDate(siteId: string | null, date: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.tasks.listByDate,
    skipQueries ? "skip" : (siteId && date ? { siteId, date } : "skip")
  ) as ConvexTask[] | undefined;
}

export function useConvexTaskById(taskId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.tasks.getTask,
    skipQueries ? "skip" : (taskId ? { taskId } : "skip")
  ) as ConvexTask | undefined;
}

// Task Mutations
export function useConvexStartTask() {
  return useMutation(convexApi.tasks.startTask);
}

export function useConvexCompleteTask() {
  return useMutation(convexApi.tasks.completeTask);
}

export function useConvexCompleteChecklistItem() {
  return useMutation(convexApi.tasks.completeChecklistItem);
}

export function useConvexCreateTask() {
  return useMutation(convexApi.tasks.createTask);
}

export function useConvexUpdateTask() {
  return useMutation(convexApi.tasks.updateTask);
}

export function useConvexAllTasks(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.tasks.listTasks,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexTask[] | undefined;
}

export function useConvexListTasks(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.tasks.listTasks,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexTask[] | undefined;
}

export function useConvexTaskChecklistItems(_taskId: string | null) {
  return undefined as ConvexTaskChecklistItem[] | undefined;
}

export function useConvexToggleChecklistItem() {
  return useMutation(convexApi.tasks.completeChecklistItem);
}

export function useConvexAddTaskPhoto() {
  try {
    return useMutation(convexApi.tasks.addTaskPhoto);
  } catch {
    return useMutation(convexApi.tasks.updateTask);
  }
}

// ============================================
// TRAINING (view/completion only)
// ============================================

export interface ConvexTrainingCourse {
  _id: string;
  siteId: string;
  title: string;
  description?: string;
  category?: string;
  modules: Array<{
    id: string;
    title: string;
    description?: string;
    sortOrder: number;
    contentType: "text" | "video" | "manual" | "quiz";
    textContent?: string;
    videoUrl?: string;
    manualId?: string;
    quizId?: string;
    estimatedMinutes?: number;
  }>;
  prerequisiteCourseIds?: string[];
  badgeId?: string;
  hasCertificate: boolean;
  certificateExpiryMonths?: number;
  passPercentage: number;
  maxAttempts?: number;
  requiredForRoles?: string[];
  isRequired: boolean;
  isActive: boolean;
  createdAt: number;
}

export interface ConvexTrainingCompletion {
  _id: string;
  siteId: string;
  courseId: string;
  staffMemberId: string;
  userId: string;
  status: "not_started" | "in_progress" | "completed" | "failed";
  moduleProgress: Array<{
    moduleId: string;
    completed: boolean;
    completedAt?: number;
    timeSpentMinutes?: number;
  }>;
  quizAttempts?: Array<{
    quizId: string;
    attemptNumber: number;
    score: number;
    maxScore: number;
    percentage: number;
    passed: boolean;
    completedAt: number;
  }>;
  completedAt?: number;
  finalScore?: number;
  passed?: boolean;
  certificateIssued?: boolean;
  certificateExpiresAt?: number;
  badgeEarned?: boolean;
  startedAt: number;
  lastActivityAt: number;
}

// Training Queries
export function useConvexTrainingCourses(siteId: string | null, category?: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.training.listCourses,
    skipQueries ? "skip" : (siteId ? { siteId, category } : "skip")
  ) as ConvexTrainingCourse[] | undefined;
}

export function useConvexTrainingCourse(courseId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.training.getCourse,
    skipQueries ? "skip" : (courseId ? { courseId } : "skip")
  ) as ConvexTrainingCourse | undefined;
}

export function useConvexMyCourses(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.training.getMyCourses,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as Array<ConvexTrainingCourse & { progress: ConvexTrainingCompletion | null }> | undefined;
}

export function useConvexMyTrainingProgress(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.training.getMyCompletions,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexTrainingCompletion[] | undefined;
}

export interface ConvexTrainingQuiz {
  _id: string;
  siteId: string;
  title: string;
  description?: string;
  questions: Array<{
    id: string;
    sortOrder: number;
    questionType: "multiple_choice" | "true_false" | "image_choice";
    question: string;
    imageId?: string;
    options: Array<{
      id: string;
      text: string;
      imageId?: string;
      isCorrect: boolean;
    }>;
    explanation?: string;
    points: number;
  }>;
  timeLimitMinutes?: number;
  shuffleQuestions: boolean;
  shuffleOptions: boolean;
  showCorrectAnswers: boolean;
  questionsPerAttempt?: number | null;
  totalPoints: number;
  isActive: boolean;
}

export function useConvexQuiz(quizId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.training.getQuiz,
    skipQueries ? "skip" : (quizId ? { quizId } : "skip")
  ) as ConvexTrainingQuiz | undefined | null;
}

// Training Mutations
export function useConvexStartCourse() {
  return useMutation(convexApi.training.startCourse);
}

export function useConvexCompleteModule() {
  return useMutation(convexApi.training.completeModule);
}

export function useConvexSubmitQuizAttempt() {
  return useMutation(convexApi.training.submitQuizAttempt);
}

// ============================================
// PROCEDURES (view/completion only)
// ============================================

export interface ConvexProcedure {
  _id: string;
  siteId: string;
  name: string;
  description?: string;
  category?: string;
  frequency: "once" | "daily" | "weekly" | "monthly" | "as_needed";
  scheduledDays?: number[];
  scheduledTime?: string;
  steps: Array<{
    id: string;
    title: string;
    instructions?: string;
    sortOrder: number;
    fieldType: "checkbox" | "text" | "number" | "temperature" | "photo" | "signature" | "select";
    selectOptions?: string[];
    isRequired: boolean;
    minValue?: number;
    maxValue?: number;
    warningThreshold?: number;
  }>;
  estimatedMinutes?: number;
  allowedRoles?: string[];
  isActive: boolean;
  createdAt: number;
}

export interface ConvexProcedureLog {
  _id: string;
  siteId: string;
  procedureId: string;
  executionDate: string;
  startedAt: number;
  completedAt?: number;
  executedByUserId: string;
  executedByName: string;
  status: "in_progress" | "completed" | "incomplete" | "failed";
  stepResponses: Array<{
    stepId: string;
    completed: boolean;
    value?: string;
    photoId?: string;
    signatureId?: string;
    notes?: string;
    timestamp: number;
    hasWarning?: boolean;
  }>;
  notes?: string;
  verifiedAt?: number;
  verifiedByUserId?: string;
}

// Procedure Queries
export function useConvexProcedures(siteId: string | null, category?: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.procedures.listProcedures,
    skipQueries ? "skip" : (siteId ? { siteId, category } : "skip")
  ) as ConvexProcedure[] | undefined;
}

export function useConvexProcedure(procedureId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.procedures.getProcedure,
    skipQueries ? "skip" : (procedureId ? { procedureId } : "skip")
  ) as ConvexProcedure | undefined;
}

export function useConvexProcedureLogs(siteId: string | null, procedureId?: string | null, date?: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.procedures.listProcedureLogs,
    skipQueries ? "skip" : (siteId ? { siteId, procedureId: procedureId || undefined, dateFrom: date, dateTo: date } : "skip")
  ) as ConvexProcedureLog[] | undefined;
}

export function useConvexDueProcedures(siteId: string | null, date?: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.procedures.getDueProcedures,
    skipQueries ? "skip" : (siteId ? { siteId, date } : "skip")
  ) as Array<ConvexProcedure & { isCompleted: boolean; todaysLog?: ConvexProcedureLog }> | undefined;
}

// Procedure Mutations
export function useConvexStartProcedure() {
  return useMutation(convexApi.procedures.startProcedure);
}

export function useConvexUpdateStepResponse() {
  return useMutation(convexApi.procedures.updateStepResponse);
}

export function useConvexCompleteProcedure() {
  return useMutation(convexApi.procedures.completeProcedure);
}

export function useConvexFailProcedure() {
  return useMutation(convexApi.procedures.failProcedure);
}

// Storage hooks
export function useConvexStorageUrl(storageId: string | null | undefined): string | null {
  const result = useQuery(
    convexApi.storage.getUrl,
    storageId ? { storageId } : "skip"
  );
  return result ?? null;
}

export function useConvexStorageUrls(storageIds: string[]): Record<string, string | null> {
  const result = useQuery(
    convexApi.storage.getUrls,
    storageIds.length > 0 ? { storageIds } : "skip"
  );
  return result ?? {};
}

// Notification Preferences
export interface ConvexNotificationPreferences {
  _id?: string;
  userId: string;
  siteId: string;
  
  // Staff settings - Shifts & Roster
  shiftReminders: boolean;
  rosterPublished: boolean;
  rosterChanges: boolean;
  
  // Staff settings - Requests
  leaveUpdates: boolean;
  swapRequests: boolean;
  
  // Staff settings - Communication
  directMessages: boolean;
  teamAnnouncements: boolean;
  
  // Staff settings - Tasks & Projects
  taskAssigned: boolean;
  taskDue: boolean;
  projectUpdates: boolean;
  projectAssigned: boolean;
  
  // Staff settings - Meetings
  meetingInvites: boolean;
  meetingReminders: boolean;
  
  // Staff settings - Reminders
  personalReminders: boolean;
  
  // Staff settings - Training
  trainingAssigned: boolean;
  trainingDue: boolean;
  
  // Staff settings - Procedures
  procedureAssigned: boolean;
  
  // Manager-specific settings
  staffLeaveSubmitted: boolean;
  staffClockInLate: boolean;
  staffOvertimeAlert: boolean;
  staffMissedClockOut: boolean;
  staffTrainingComplete: boolean;
  staffProcedureComplete: boolean;
  swapTargetResponse: boolean;
  
  // Timing settings
  shiftReminderTiming: "30min" | "1hr" | "2hr" | "day";
  meetingReminderTiming: "15min" | "30min" | "1hr";
  
  createdAt?: number;
  updatedAt?: number;
}

export const DEFAULT_NOTIFICATION_PREFERENCES: Omit<ConvexNotificationPreferences, "_id" | "userId" | "siteId" | "createdAt" | "updatedAt"> = {
  // Staff settings - enabled by default
  shiftReminders: true,
  rosterPublished: true,
  rosterChanges: true,
  leaveUpdates: true,
  swapRequests: true,
  directMessages: true,
  teamAnnouncements: false, // Can be noisy
  taskAssigned: true,
  taskDue: true,
  projectUpdates: true,
  projectAssigned: true,
  meetingInvites: true,
  meetingReminders: true,
  personalReminders: true,
  trainingAssigned: true,
  trainingDue: true,
  procedureAssigned: true,
  
  // Manager settings - enabled by default
  staffLeaveSubmitted: true,
  staffClockInLate: true,
  staffOvertimeAlert: true,
  staffMissedClockOut: true,
  staffTrainingComplete: true,
  staffProcedureComplete: true,
  swapTargetResponse: true,
  
  // Default timing
  shiftReminderTiming: "1hr",
  meetingReminderTiming: "30min",
};

export function useConvexNotificationPreferences(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.notificationPreferences.get,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  ) as ConvexNotificationPreferences | null | undefined;
}

export function useConvexUpsertNotificationPreferences() {
  return useMutation(convexApi.notificationPreferences.upsert);
}

// ============================================
// STAFF SHIFTS (individual staff member shifts)
// ============================================

export function useConvexStaffShifts(
  staffMemberId: string | null,
  startDate?: string,
  endDate?: string
) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.staff.getStaffShifts,
    skipQueries ? "skip" : (staffMemberId ? { staffMemberId, startDate, endDate } : "skip")
  );
}

// ============================================
// PROJECT GANTT & NOTES
// ============================================

export function useConvexGanttData(projectId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.projects.getGanttData,
    skipQueries ? "skip" : (projectId ? { projectId } : "skip")
  );
}

export function useConvexProjectNotes(projectId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.projects.listProjectNotes,
    skipQueries ? "skip" : (projectId ? { projectId } : "skip")
  );
}

// ============================================
// TASK CHECKLIST (alias for task-detail-sheet)
// ============================================

export function useConvexTaskChecklist(taskId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.tasks.getTaskChecklistItems,
    skipQueries ? "skip" : (taskId ? { taskId } : "skip")
  );
}

// ============================================
// TRAINING BADGES & COURSES
// ============================================

export function useConvexMyBadges(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.training.getMyBadges,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  );
}

export function useConvexCourses(siteId: string | null, category?: string) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.training.listCourses,
    skipQueries ? "skip" : (siteId ? { siteId, category } : "skip")
  );
}

// ============================================
// MESSAGE BOARDS (deprecated - kept for notice-board-widget compatibility)
// ============================================

export function useConvexMessageBoards(siteId: string | null) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.messaging.listBoards,
    skipQueries ? "skip" : (siteId ? { siteId } : "skip")
  );
}

export function useConvexBoardMessages(boardId: string | null, limit: number = 50) {
  const skipQueries = shouldSkipConvexQueries();
  return useQuery(
    convexApi.messaging.listBoardMessages,
    skipQueries ? "skip" : (boardId ? { boardId, limit } : "skip")
  );
}
